import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Receipt,
  RefreshCw,
  ShieldAlert,
  ChevronRight,
  TrendingUp,
  CreditCard,
  Calendar,
  User,
  Hash,
  Info,
  Clock,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useAdminAuth } from "../../lib/use-admin-auth";
import { cn } from "../../lib/utils";
import { formatMoney } from "../../lib/formatters";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type AccountingDocument = {
  id: string;
  documentNumber: string | null;
  documentType: string;
  status: string;
  direction: string;
  currency: string;
  issueDate: string | null;
  dueDate: string | null;
  sentAt: string | null;
  paidAt: string | null;
  documentUrl: string | null;
  customerName: string | null;
  customerOrgNumber: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  subtotalAmount: number;
  vatAmount: number;
  totalAmount: number;
  outstandingAmount: number | null;
  notes: string | null;
  externalSource: string | null;
  rawData: Record<string, unknown>;
  createdAt: string;
  vehicle: {
    id: string;
    registrationNumber: string | null;
    make: string;
    model: string;
  } | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    orgNumber: string | null;
    phone: string | null;
  } | null;
  auction: {
    id: string;
    referenceNumber: string;
    title: string;
    status: string;
  } | null;
  orderLines: Array<{
    id: string;
    description: string;
    lineType: string;
    status: string;
    quantity: number;
    unitPriceAmount: number;
    netAmount: number;
    vatAmount: number;
    totalAmount: number;
    currency: string;
    externalReference: string | null;
    createdAt: string;
    invoicedAt: string | null;
  }>;
};

function getDocumentTypeLabel(documentType: string) {
  if (documentType === "credit_note") return "Kreditnota";
  if (documentType === "purchase_settlement") return "Kjøpsoppgjør";
  return "Faktura";
}

function formatDate(value: string | null, withTime = false) {
  if (!value) return "—";
  return new Date(value).toLocaleString(
    "nb-NO",
    withTime
      ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
      : { day: "2-digit", month: "short", year: "numeric" },
  );
}

function getStatusConfig(status: string) {
  switch (status) {
    case "paid":
    case "invoiced":
      return { label: "Betalt", icon: CheckCircle2, color: "text-green-600 bg-green-50 border-green-100" };
    case "sent":
    case "payment_pending":
      return { label: "Venter betaling", icon: Clock, color: "text-blue-600 bg-blue-50 border-blue-100" };
    case "failed":
      return { label: "Feilet", icon: AlertCircle, color: "text-red-600 bg-red-50 border-red-100" };
    case "queued":
      return { label: "I kø", icon: Loader2, color: "text-amber-600 bg-amber-50 border-amber-100" };
    default:
      return { label: status, icon: Info, color: "text-gray-600 bg-gray-50 border-gray-100" };
  }
}

export function RegnskapDetailPage() {
  const { accountingdocumentid } = useParams<{ accountingdocumentid: string }>();
  const navigate = useNavigate();
  const { profile } = useAdminAuth();
  const canViewRawData = profile?.isSuperAdmin === true && profile.superAdminRole === "developer";
  const [document, setDocument] = useState<AccountingDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);
  const [showRawData, setShowRawData] = useState(false);

  const fetchDocument = async () => {
    if (!accountingdocumentid) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/api/admin/accounting/documents/${accountingdocumentid}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Kunne ikke hente regnskapsdokumentet");
      }

      const payload = await response.json() as AccountingDocument;
      setDocument(payload);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Noe gikk galt");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchDocument();
  }, [accountingdocumentid]);

  const fikenMeta = useMemo(() => {
    const raw = document?.rawData ?? {};
    return {
      invoiceId: typeof raw.fikenInvoice === "object" && raw.fikenInvoice !== null && "invoiceId" in raw.fikenInvoice
        ? String((raw.fikenInvoice as Record<string, unknown>).invoiceId)
        : null,
      deliveryMethod: typeof raw.deliveryMethod === "string" ? raw.deliveryMethod : null,
      lastError: typeof raw.lastError === "string" ? raw.lastError : null,
    };
  }, [document]);

  const handleDownload = () => {
    if (!accountingdocumentid) return;
    window.location.href = `${API}/api/admin/accounting/documents/${accountingdocumentid}/download`;
  };

  const handleResync = async () => {
    if (!accountingdocumentid) return;

    setResyncing(true);
    setResyncMessage(null);

    try {
      const response = await fetch(`${API}/api/admin/accounting/documents/${accountingdocumentid}/resync`, {
        method: "POST",
        credentials: "include",
      });

      const payload = await response.json().catch(() => null) as { error?: string } | null;

      if (!response.ok) {
        throw new Error(payload?.error || "Kunne ikke starte resync fra Fiken");
      }

      setResyncMessage("Resync startet. Oppdaterer dokumentet...");
      await fetchDocument();
    } catch (nextError) {
      setResyncMessage(nextError instanceof Error ? nextError.message : "Noe gikk galt under resync");
    } finally {
      setResyncing(false);
      setTimeout(() => setResyncMessage(null), 5000);
    }
  };

  const handleReverse = async () => {
    if (!accountingdocumentid) return;

    setReversing(true);
    setResyncMessage(null);

    try {
      const response = await fetch(`${API}/api/admin/accounting/documents/${accountingdocumentid}/reverse`, {
        method: "POST",
        credentials: "include",
      });

      const payload = await response.json().catch(() => null) as { error?: string; documentId?: string } | null;

      if (!response.ok) {
        throw new Error(payload?.error || "Kunne ikke opprette kreditnota");
      }

      if (payload?.documentId) {
        navigate(`/regnskap/${payload.documentId}`);
        return;
      }

      setResyncMessage("Kreditnota opprettet.");
      await fetchDocument();
    } catch (nextError) {
      setResyncMessage(nextError instanceof Error ? nextError.message : "Noe gikk galt under kreditering");
    } finally {
      setReversing(false);
      setTimeout(() => setResyncMessage(null), 5000);
    }
  };

  if (loading && !document) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
        <p className="text-muted-foreground animate-pulse font-medium">Henter regnskapsdetaljer...</p>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="px-4 py-12 max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate("/regnskap")} className="gap-2 group">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Tilbake til oversikt
        </Button>
        <Card className="border-red-200 bg-red-50/50 backdrop-blur-sm shadow-xl shadow-red-500/5 overflow-hidden">
          <div className="h-1.5 bg-red-500 w-full" />
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <ShieldAlert className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-red-900 font-black">Beskyttet feil</CardTitle>
                <CardDescription className="text-red-700/70 font-medium">{error ?? "Dokumentet ble ikke funnet eller er utilgjengelig."}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => window.location.reload()} className="w-full border-red-200 hover:bg-red-100 text-red-700">
              Prøv på nytt
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = getStatusConfig(document.status);
  const canReverse = document.documentType === "invoice" && document.status !== "queued" && document.status !== "failed";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* Breadcrumbs & Simple Nav */}
      <nav className="flex items-center gap-2 text-sm font-bold text-muted-foreground mb-2">
        <Link to="/regnskap" className="hover:text-primary transition-colors flex items-center gap-1.5">
          <Receipt className="h-4 w-4" />
          Regnskap
        </Link>
        <ChevronRight className="h-3 w-3 opacity-40" />
        <span className="text-primary/70">{getDocumentTypeLabel(document.documentType)}</span>
        <ChevronRight className="h-3 w-3 opacity-40" />
        <span className="text-primary truncate max-w-[150px]">{document.documentNumber || "Utkast"}</span>
      </nav>

      {/* Main Header Card with Glassmorphism */}
      <div className="relative overflow-hidden rounded-[32px] bg-white border border-border shadow-2xl shadow-primary/5 p-8 lg:p-10">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary text-white shadow-lg shadow-primary/20">
                <Receipt className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-black tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
                  {document.documentNumber || "Dokumentutkast"}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className={cn("text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border shadow-sm", statusConfig.color)}>
                    {statusConfig.label}
                  </span>
                  <span className="text-xs font-bold text-muted-foreground/60 px-2 py-1 bg-muted rounded-md border border-border/50">
                    {getDocumentTypeLabel(document.documentType)}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-lg text-muted-foreground/80 font-medium max-w-2xl">
              Utstedt til <span className="text-foreground font-black">{document.customerName}</span> for handelen knyttet til
              {document.auction ? (
                <Link to={`/auksjoner/${document.auction.id}`} className="text-accent hover:underline decoration-2 underline-offset-4 ml-1.5 inline-flex items-center gap-1">
                  {document.auction.referenceNumber} <ExternalLink className="h-3 w-3" />
                </Link>
              ) : "en ukjent auksjon"}.
            </p>
            {document.vehicle?.registrationNumber && (
              <p className="text-sm font-bold text-muted-foreground">
                Reg.nr. <span className="text-foreground">{document.vehicle.registrationNumber}</span>
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {canReverse && (
              <Button
                variant="outline"
                className="h-12 px-6 rounded-2xl border-border/60 hover:border-primary/20 hover:bg-primary/5 font-bold transition-all shadow-sm"
                onClick={handleReverse}
                disabled={reversing}
              >
                {reversing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Opprett kreditnota
              </Button>
            )}
            <Button
              variant="outline"
              className="h-12 px-6 rounded-2xl border-border/60 hover:border-primary/20 hover:bg-primary/5 font-bold transition-all shadow-sm"
              onClick={handleResync}
              disabled={resyncing}
            >
              {resyncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Synkroniser
            </Button>

            {document.documentUrl && (
              <>
                <Button
                  variant="outline"
                  className="h-12 px-6 rounded-2xl border-border/60 hover:border-primary/20 hover:bg-primary/5 font-bold transition-all shadow-sm"
                  onClick={() => window.open(document.documentUrl!, "_blank", "noopener,noreferrer")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Vis PDF
                </Button>
                <Button
                  className="h-12 px-8 rounded-2xl bg-primary text-white font-black transition-all shadow-xl shadow-primary/20 hover:opacity-90 active:scale-[0.98]"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Last ned
                </Button>
              </>
            )}
          </div>
        </div>

        {resyncMessage && (
          <div className="mt-8 p-4 rounded-2xl bg-accent/5 border border-accent/10 flex items-center gap-3 text-accent animate-in fade-in zoom-in duration-300">
            <Info className="h-5 w-5" />
            <p className="text-sm font-bold">{resyncMessage}</p>
          </div>
        )}
      </div>

      {/* Financial Summary Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          label="Totalbeløp"
          value={formatMoney(document.totalAmount, document.currency)}
          description={`Eks. mva ${formatMoney(document.subtotalAmount, document.currency)}`}
          icon={TrendingUp}
          color="accent"
        />
        <SummaryCard
          label="Utestående"
          value={formatMoney(document.outstandingAmount ?? (document.status === "paid" ? 0 : document.totalAmount), document.currency)}
          description={document.status === "paid" ? "Fullt oppgjort" : "Venter på innbetaling"}
          icon={CreditCard}
          color={document.status === "paid" ? "green" : "blue"}
          highlighted={document.status !== "paid"}
        />
        <SummaryCard
          label="Utstedt dato"
          value={formatDate(document.issueDate)}
          description={`Forfaller ${formatDate(document.dueDate)}`}
          icon={Calendar}
          color="gray"
        />
        <SummaryCard
          label="Kundereferanse"
          value={document.customerOrgNumber || "Ingen org.nr"}
          description={document.customerEmail || "Ingen e-post"}
          icon={User}
          color="gray"
        />
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

        {/* Left Column: Line Items */}
        <div className="xl:col-span-2 space-y-6">
          <Card className="rounded-[32px] border-border overflow-hidden shadow-xl shadow-primary/5 transition-all hover:shadow-primary/10">
            <CardHeader className="p-8 border-b border-border/50 bg-muted/20">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-black">Spesifisert fakturagrunnlag</CardTitle>
                  <CardDescription className="text-sm font-medium mt-1">Gjennomgang av alle ordrelinjer og avgifter</CardDescription>
                </div>
                <Badge variant="secondary" className="h-8 px-3 rounded-full font-bold bg-white text-primary border border-border">
                  {document.orderLines.length} {document.orderLines.length === 1 ? "linje" : "linjer"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left bg-muted/30 text-[10px] uppercase font-black tracking-widest text-muted-foreground/60 border-b border-border/50">
                      <th className="px-8 py-4">Beskrivelse</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-center">Antall</th>
                      <th className="px-6 py-4 text-right">MVA</th>
                      <th className="px-8 py-4 text-right">Beløp (inkl. mva)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {document.orderLines.map((line) => (
                      <tr key={line.id} className="group hover:bg-muted/30 transition-colors">
                        <td className="px-8 py-6">
                          <p className="font-black text-[15px] group-hover:text-primary transition-colors">{line.description}</p>
                          <p className="text-[11px] font-black uppercase text-muted-foreground/50 tracking-wider mt-1.5 flex items-center gap-1.5">
                            <span className="px-1.5 py-0.5 bg-muted rounded uppercase">{line.lineType}</span>
                            {line.externalReference && (
                              <>
                                <span>·</span>
                                <span className="font-mono">{line.externalReference}</span>
                              </>
                            )}
                          </p>
                        </td>
                        <td className="px-6 py-6">
                          <span className={cn(
                            "text-[9px] font-black uppercase px-2 py-0.5 rounded-full border tracking-wider",
                            line.status === "invoiced" ? "bg-green-50 text-green-700 border-green-100" : "bg-gray-50 text-gray-500 border-gray-100"
                          )}>
                            {line.status}
                          </span>
                        </td>
                        <td className="px-6 py-6 text-center font-bold text-sm">
                          {line.quantity}
                        </td>
                        <td className="px-6 py-6 text-right font-medium text-muted-foreground text-sm">
                          {formatMoney(line.vatAmount, line.currency)}
                        </td>
                        <td className="px-8 py-6 text-right">
                          <p className="font-black text-primary text-base">{formatMoney(line.totalAmount, line.currency)}</p>
                          <p className="text-[10px] font-bold text-muted-foreground/50 mt-1">
                            {formatMoney(line.unitPriceAmount, line.currency)} /stk
                          </p>
                        </td>
                      </tr>
                    ))}
                    {/* Calculation rows */}
                    <tr className="bg-muted/10">
                      <td colSpan={3} className="px-8 py-10" />
                      <td className="px-6 py-10 text-right space-y-3">
                        <p className="text-[11px] font-black uppercase text-muted-foreground tracking-widest">Delsum</p>
                        <p className="text-[11px] font-black uppercase text-muted-foreground tracking-widest">MVA</p>
                        <p className="text-xl font-black text-primary border-t border-border/50 pt-3 mt-3">Total</p>
                      </td>
                      <td className="px-8 py-10 text-right space-y-3">
                        <p className="font-bold">{formatMoney(document.subtotalAmount, document.currency)}</p>
                        <p className="font-bold">{formatMoney(document.vatAmount, document.currency)}</p>
                        <p className="text-2xl font-black text-primary border-t border-border/50 pt-3 mt-3">{formatMoney(document.totalAmount, document.currency)}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Raw Metadata Explorer with specialized code styling */}
          {canViewRawData && (
            <Card className="rounded-[32px] border-border overflow-hidden shadow-xl shadow-primary/5">
              <button
                onClick={() => setShowRawData(!showRawData)}
                className="w-full flex items-center justify-between p-8 hover:bg-muted/20 transition-all font-black text-lg text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-900 rounded-lg">
                    <Hash className="h-5 w-5 text-slate-400" />
                  </div>
                  <span>Utvidet Dokumentmetadata</span>
                </div>
                <ChevronRight className={cn("h-5 w-5 transition-transform duration-300", showRawData ? "rotate-90" : "")} />
              </button>
              {showRawData && (
                <CardContent className="px-8 pb-8 pt-0 animate-in slide-in-from-top-4 duration-500">
                  <div className="relative group">
                    <div className="absolute top-4 right-4 z-10">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[10px] font-black uppercase text-slate-500 hover:text-white hover:bg-slate-700 bg-slate-800/50 backdrop-blur-md px-3 h-7 rounded-lg"
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(document.rawData, null, 2));
                        }}
                      >
                        Kopier JSON
                      </Button>
                    </div>
                    <pre className="overflow-x-auto rounded-3xl bg-[#0F172A] p-6 text-[12px] leading-relaxed text-slate-300 font-mono border border-slate-800 shadow-inner max-h-[500px] scrollbar-thin scrollbar-thumb-slate-700">
                      {JSON.stringify(document.rawData, null, 2)}
                    </pre>
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>

        {/* Right Column: Party Info & Notes */}
        <div className="space-y-8">

          {/* Organization & Customer Cards */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 ml-2">Involverte parter</h3>

            {/* Sender (Always Forhandlerbørsen) */}
            <Card className="rounded-3xl border-border shadow-lg shadow-primary/5 hover:border-primary/20 transition-all bg-slate-50/50">
              <CardHeader className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center border border-primary/10 shadow-lg shadow-primary/10">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-black leading-tight text-primary">Utsteder</CardTitle>
                    <span className="text-sm font-black text-foreground">Forhandlerbørsen AS</span>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Counterparty (Buyer/Seller) */}
            <Card className="rounded-3xl border-border shadow-lg shadow-primary/5 border-l-4 border-l-accent overflow-hidden hover:border-accent/40 transition-all">
              <CardHeader className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-accent/5 flex items-center justify-center border border-accent/10 shadow-inner">
                    <User className="h-6 w-6 text-accent" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base font-black leading-tight">Motpart</CardTitle>
                    {document.organization ? (
                      <Link to={`/organizations/${document.organization.id}`} className="text-sm font-bold text-primary hover:underline">
                        {document.organization.name}
                      </Link>
                    ) : <span className="text-sm font-bold text-muted-foreground">{document.customerName || "Ukjent kunde"}</span>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6 space-y-4 pt-0">
                <div className="pt-4 border-t border-border/50 grid gap-3">
                  <LabelValue label="Organisasjonsnummer" value={document.customerOrgNumber || "Ikke oppgitt"} />
                  <LabelValue label="E-post" value={document.customerEmail || "Ikke oppgitt"} copyable />
                  <LabelValue label="Telefon" value={document.customerPhone || "Ikke oppgitt"} />
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Integration Status & Facts */}
          <Card className="rounded-[32px] border-border shadow-xl shadow-primary/5 overflow-hidden">
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Systemfakta & Integrasjon</span>
              <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
            </div>
            <CardContent className="p-6 space-y-5">
              <LabelValue label="Dokumenttype" value={getDocumentTypeLabel(document.documentType)} />
              <LabelValue label="Retning" value={document.direction === "outbound" ? "Utgående" : "Inngående"} />
              <LabelValue label="Registreringsnummer" value={document.vehicle?.registrationNumber || "Ikke oppgitt"} />
              <LabelValue label="Kilde" value={document.externalSource || "Forhandlerbørsen Core"} />
              {fikenMeta.deliveryMethod && <LabelValue label="Leveransemetode" value={fikenMeta.deliveryMethod} />}
              {fikenMeta.invoiceId && <LabelValue label="Ekstern ID (Fiken)" value={fikenMeta.invoiceId} />}
              <div className="pt-4 border-t border-border/50 space-y-3">
                <LabelValue label="Sist endret" value={formatDate(document.createdAt, true)} />
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground font-medium underline underline-offset-4 decoration-dotted">Intern ID</span>
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-[10px]">{document.id}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes Section if available */}
          {document.notes && (
            <Card className="rounded-[32px] border-accent/20 bg-accent/5 shadow-lg shadow-accent/5">
              <CardHeader className="p-6 pb-2">
                <div className="flex items-center gap-2 text-accent">
                  <FileText className="h-5 w-5" />
                  <CardTitle className="text-sm font-black uppercase tracking-widest">Notater</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <p className="text-sm font-medium text-accent/80 leading-relaxed italic">
                  {document.notes}
                </p>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  description,
  icon: Icon,
  color = "gray",
  highlighted = false
}: {
  label: string;
  value: string | number;
  description: string;
  icon: any;
  color?: "accent" | "green" | "blue" | "gray";
  highlighted?: boolean;
}) {
  const themes = {
    accent: "text-accent bg-accent/5 border-accent/10 shadow-accent/5",
    green: "text-green-600 bg-green-50 border-green-100 shadow-green-500/5",
    blue: "text-blue-600 bg-blue-50 border-blue-100 shadow-blue-500/5",
    gray: "text-primary/70 bg-muted/30 border-border/50 shadow-primary/5",
  };

  return (
    <Card className={cn(
      "rounded-[32px] border-border shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 overflow-hidden",
      highlighted && "border-primary/20 bg-primary/5 ring-1 ring-primary/10"
    )}>
      <CardContent className="p-8">
        <div className="flex items-start justify-between">
          <div className={cn("p-2.5 rounded-2xl border", themes[color])}>
            <Icon className="h-5 w-5" />
          </div>
          {highlighted && (
            <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
          )}
        </div>
        <div className="mt-5 space-y-1">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">{label}</p>
          <p className="text-2xl font-black tracking-tight">{value}</p>
          <p className="text-xs font-bold text-muted-foreground/50">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function LabelValue({ label, value, copyable = false }: { label: string; value: string; copyable?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">{label}</span>
      <div className="flex items-center justify-between gap-2 group">
        <span className="text-sm font-bold truncate max-w-full text-foreground/90">{value}</span>
        {copyable && value !== "Ikke oppgitt" && (
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded text-accent"
            onClick={() => navigator.clipboard.writeText(value)}
          >
            <Hash className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
